import {
  ComponentType,
  ForwardRefExoticComponent,
  PropsWithoutRef,
  ReactElement,
  RefAttributes,
  forwardRef,
  useEffect
} from 'react'
import { MFESurface, createMFERuntimeLogger } from './mfeLogger'

interface BoundaryArgs<P> {
  mfe: string
  surface: MFESurface
  RuntimeComponent: ComponentType<P>
  BuildTimeComponent: ComponentType<P>
}

interface MFERuntimeBoundary<P, R = unknown> {
  Runtime: ForwardRefExoticComponent<PropsWithoutRef<P> & RefAttributes<R>>
  BuildTime: ForwardRefExoticComponent<
    PropsWithoutRef<P & { isRuntimeEnabled: boolean }> & RefAttributes<R>
  >
  onRuntimeError: (error: Error) => void
}

/**
 * Creates a matched pair of wrapper components plus an error handler for an
 * MFE that supports both a runtime (module federation) and build-time fallback.
 *
 * - `Runtime` logs a one-time success the first time it mounts.
 * - `BuildTime` logs a one-time build-time fallback event the first time it
 *   mounts, including whether the runtime feature flag was enabled.
 * - `onRuntimeError` is intended for `<ErrorBoundary onError={...} />`.
 *
 * Both wrappers are `forwardRef` so imperative handles exposed by the
 * underlying MFE component (e.g. MAP's `MakeAPaymentHandle.requestClose`)
 * are preserved through the wrapper. The generic `R` parameter types that
 * handle.
 *
 * Each call site gets its own closure-scoped "logged once" flags, mirroring
 * the previous module-level booleans on a per-MFE basis.
 *
 * Usage:
 *   const { Runtime, BuildTime, onRuntimeError } = createMFERuntimeBoundary({
 *     mfe: 'make-a-payment',
 *     surface: 'modal',
 *     RuntimeComponent: lazy(() => import('make-payment-mfe/MakeAPayment')),
 *     BuildTimeComponent: PaymentModal
 *   })
 */
export const createMFERuntimeBoundary = <P extends object, R = unknown>({
  mfe,
  surface,
  RuntimeComponent,
  BuildTimeComponent
}: BoundaryArgs<P>): MFERuntimeBoundary<P, R> => {
  const logger = createMFERuntimeLogger(mfe, surface)
  let hasLoggedRuntime = false
  let hasLoggedBuildTime = false

  const Runtime = forwardRef<R, P>((props, ref): ReactElement => {
    useEffect(() => {
      if (hasLoggedRuntime) return

      hasLoggedRuntime = true
      logger.logSuccess()
    }, [])

    /*
     * RuntimeComponent may or may not accept a ref. When it doesn't, React
     * will warn — that's fine, the wrapper itself shouldn't strip it.
     */
    return <RuntimeComponent {...(props as P)} ref={ref as never} />
  })
  Runtime.displayName = `MFERuntime(${mfe}:${surface})`

  const BuildTime = forwardRef<R, P & { isRuntimeEnabled: boolean }>(
    ({ isRuntimeEnabled, ...rest }, ref): ReactElement => {
      useEffect(() => {
        if (hasLoggedBuildTime) return

        hasLoggedBuildTime = true
        logger.logBuildTime(isRuntimeEnabled)
      }, [isRuntimeEnabled])

      return (
        <BuildTimeComponent {...(rest as unknown as P)} ref={ref as never} />
      )
    }
  )
  BuildTime.displayName = `MFEBuildTime(${mfe}:${surface})`

  const onRuntimeError = (error: Error): void => {
    logger.logError(error)
  }

  return {
    Runtime,
    BuildTime: BuildTime as MFERuntimeBoundary<P, R>['BuildTime'],
    onRuntimeError
  }
}
